import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useEffect, useState } from "react";
import { api, roleLabel, fmtDateTime } from "@/lib/api";
import { LogOut } from "lucide-react";

const NAV = {
  borrower: [
    { to: "/app", label: "Dashboard", end: true },
    { to: "/app/qualify", label: "What I qualify for" },
    { to: "/app/applications", label: "Applications" },
    { to: "/app/documents", label: "Documents" },
    { to: "/app/programs", label: "Loan Programs" },
    { to: "/app/settings", label: "Preferences" },
  ],
  broker: [
    { to: "/app", label: "Dashboard", end: true },
    { to: "/app/qualify", label: "Qualify clients" },
    { to: "/app/applications", label: "Submissions" },
    { to: "/app/programs", label: "Loan Programs" },
    { to: "/app/settings", label: "Preferences" },
  ],
  loan_rep: [
    { to: "/app", label: "Book", end: true },
    { to: "/app/applications", label: "Files" },
    { to: "/app/programs", label: "Loan Programs" },
    { to: "/app/settings", label: "Preferences" },
  ],
  admin: [
    { to: "/app", label: "Overview", end: true },
    { to: "/app/applications", label: "Files" },
    { to: "/app/users", label: "Personnel" },
    { to: "/app/programs", label: "Loan Programs" },
    { to: "/app/settings", label: "Preferences" },
  ],
};

function Sidebar() {
  const { user, logout } = useAuth();
  const items = NAV[user?.role] || NAV.borrower;
  const roleTitle = { borrower: "Business Owner", broker: "Advisor", loan_rep: "Loan Officer", admin: "Partner" }[user?.role] || "";
  const [pinned, setPinned] = useState([]);
  const loadPinned = () =>
    api.get("/deal-views")
      .then((r) => setPinned((r.data || []).filter((v) => v.pinned)))
      .catch(() => setPinned([]));
  useEffect(() => {
    loadPinned();
    const onChange = () => loadPinned();
    window.addEventListener("vn:views-changed", onChange);
    return () => window.removeEventListener("vn:views-changed", onChange);
  }, []);

  return (
    <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col" data-testid="sidebar">
      <div className="px-6 py-7 border-b border-slate-200">
        <Logo />
        <div className="mt-1 small-caps text-slate-400">{roleTitle}</div>
      </div>
      <nav className="flex-1 px-4 py-6 space-y-1">
        {items.map((it) => (
          <div key={it.to}>
            <NavLink
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center justify-between px-3 py-2 small-caps transition-colors ${
                  isActive
                    ? "text-[#1F2937] bg-slate-800/[0.06]"
                    : "text-slate-500 hover:text-[#1F2937]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{it.label}</span>
                  {isActive && <span className="text-[#1F2937]">·</span>}
                </>
              )}
            </NavLink>
            {/* Pinned saved views appear directly under the Files entry */}
            {(it.to === "/app/applications") && pinned.length > 0 && (
              <div className="mt-1 mb-2 ml-3 pl-3 border-l border-slate-200 space-y-0.5" data-testid="sidebar-pinned-views">
                {pinned.map((v) => (
                  <NavLink
                    key={v.id}
                    to={`/app/applications?view=${v.id}`}
                    data-testid={`sidebar-pinned-view-${v.id}`}
                    className="block px-3 py-1.5 text-[12px] text-slate-500 hover:text-[#1F2937] truncate"
                    title={v.name}
                  >
                    {v.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
      <div className="border-t border-slate-200 p-4 flex items-center gap-3">
        <div className="h-9 w-9 rounded-none bg-slate-800 text-white font-semibold text-[15px] grid place-items-center">
          {(user?.name || "?").split(" ").map((s) => s[0]).slice(0,2).join("")}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-[15px] text-[#1F2937]">{user?.name}</div>
          <div className="truncate small-caps text-slate-500 text-[10px]">{roleLabel(user?.role)}</div>
        </div>
        <button onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-[#1F2937]" title="Sign out">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const unread = items.filter((i) => !i.read).length;
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative small-caps text-slate-500 hover:text-[#1F2937] flex items-center gap-2" data-testid="notifications-bell">
          <span>Alerts</span>
          {unread > 0 && (
            <span className="inline-flex h-4 min-w-[16px] px-1 items-center justify-center bg-slate-800 text-white text-[10px] font-mono" data-testid="notifications-count">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-none border-slate-200 bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="small-caps text-slate-500">Alerts</div>
          {unread > 0 && (
            <button onClick={readAll} className="small-caps text-slate-500 hover:text-[#1F2937]" data-testid="notif-mark-all">Mark all read →</button>
          )}
        </div>
        <div className="max-h-96 overflow-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center small-caps text-slate-400">Nil.</div>
          ) : items.map((n) => (
            <div key={n.id} className={`px-5 py-4 border-b border-[#1F2937]/10 ${n.read ? "" : "bg-slate-800/[0.06]"}`}>
              <div className="font-semibold text-[16px] text-[#1F2937]">{n.title}</div>
              {n.body && <div className="text-[13px] text-slate-500 mt-1">{n.body}</div>}
              <div className="mt-2 small-caps text-slate-400">{fmtDateTime(n.created_at)}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ applications: [], users: [] });
  const nav = useNavigate();
  useEffect(() => {
    if (q.length < 2) { setResults({ applications: [], users: [] }); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative w-96 max-w-full">
      <div className="small-caps text-slate-400 absolute left-0 top-2">Search</div>
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="File number, principal, sector…"
        data-testid="global-search-input"
        className="pl-16 border-0 border-b border-slate-300 rounded-none bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#1F2937] font-semibold text-[16px] h-9"
      />
      {open && q.length >= 2 && (
        <div className="absolute top-full mt-1 w-[28rem] max-w-[80vw] border border-slate-200 bg-white shadow-xl z-50 max-h-[70vh] overflow-auto">
          {results.applications.length === 0 && results.users.length === 0 && (
            <div className="p-4 small-caps text-slate-400">No matches.</div>
          )}
          {results.applications.length > 0 && (
            <div className="px-4 py-2 small-caps text-[10px] text-slate-400 border-b border-slate-100 bg-slate-50">
              Files · {results.applications.length}
            </div>
          )}
          {results.applications.map((a) => (
            <button
              key={a.id}
              onMouseDown={() => nav(`/app/applications/${a.id}`)}
              className="w-full text-left px-4 py-3 hover:bg-slate-800/[0.04] border-b border-[#1F2937]/10"
              data-testid={`search-result-${a.id}`}
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="font-mono text-[11px] text-slate-500">{a.loan_number}</div>
                <div className="small-caps text-[10px] text-slate-400">
                  {a.loan_rep_name || "Unassigned"}
                </div>
              </div>
              <div className="font-semibold text-[15px] text-[#1F2937] mt-0.5">
                {a.business_name || a.product_name || "—"}
              </div>
              {a.business_name && a.product_name && (
                <div className="small-caps text-[11px] text-slate-500 mt-0.5">
                  {a.product_name}
                </div>
              )}
            </button>
          ))}
          {results.users.length > 0 && (
            <div className="px-4 py-2 small-caps text-[10px] text-slate-400 border-b border-slate-100 bg-slate-50">
              People · {results.users.length}
            </div>
          )}
          {results.users.slice(0, 8).map((u) => (
            <div key={u.id} className="px-4 py-3 border-b border-[#1F2937]/10">
              <div className="font-semibold text-[15px] text-[#1F2937]">{u.business_name || u.name}</div>
              <div className="text-[12px] text-slate-500">{u.email} · {roleLabel(u.role)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TopBar() {
  const { user, logout } = useAuth();
  return (
    <div className="sticky top-0 z-30 flex items-center justify-between gap-6 px-8 py-4 border-b border-slate-200 bg-white/90 backdrop-blur">
      <GlobalSearch />
      <div className="flex items-center gap-8">
        <NotificationsBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 small-caps text-slate-600 hover:text-[#1F2937]" data-testid="user-menu-btn">
              <div className="h-7 w-7 bg-slate-800 text-white font-semibold text-[12px] grid place-items-center">
                {(user?.name || "?").split(" ").map(s=>s[0]).slice(0,2).join("")}
              </div>
              <span>{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 rounded-none border-slate-200 bg-white">
            <DropdownMenuLabel className="small-caps text-slate-500">{roleLabel(user?.role)}</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-slate-800/10" />
            <DropdownMenuItem onClick={logout} data-testid="user-menu-logout" className="small-caps"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  return (
    <div className="min-h-screen flex bg-white">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <ImpersonationBanner />
        <TopBar />
        <main className="flex-1 min-w-0 px-10 py-12" data-testid="dashboard-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function ImpersonationBanner() {
  const { user, isImpersonating, returnToAdmin } = useAuth();
  if (!isImpersonating || !user) return null;
  return (
    <div
      className="bg-[#1F2937] text-white px-10 py-3 flex items-center justify-between border-b border-black"
      data-testid="impersonation-banner"
    >
      <div className="text-[13px] font-semibold tracking-tight">
        You are signed in as <span className="underline underline-offset-2">{user.name || user.email}</span>
        <span className="ml-3 small-caps text-white/70">
          {user._impersonated_by_name ? `by ${user._impersonated_by_name}` : "admin session"}
        </span>
      </div>
      <button
        onClick={returnToAdmin}
        data-testid="return-to-admin-btn"
        className="group inline-flex items-center gap-3 text-white"
      >
        <span className="h-[1px] w-8 bg-white transition-all group-hover:w-12" />
        <span className="small-caps">Return to admin</span>
      </button>
    </div>
  );
}
